import {useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {LockGlyph} from '@/components/ui/icons'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Tag} from '@/components/ui/tag'
import {KairosMark} from '@/components/kairos-mark'
import {UPGRADE_CONTACT_EMAIL} from '@/lib/constants'

function upgradeMailto(businessName?: string) {
  const subject = businessName ? `Quiero Kairos Pro para ${businessName}` : 'Quiero Kairos Pro'
  return `mailto:${UPGRADE_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`
}

function UpsellCta({testId, businessId, businessName}: {testId: string; businessId?: string; businessName?: string}) {
  const {t} = useTranslation()
  if (businessId) {
    return (
      <Button data-testid={testId} asChild>
        <Link to={`/businesses/${businessId}/plan`}>{t('plans.cta')}</Link>
      </Button>
    )
  }
  return (
    <Button data-testid={testId} asChild>
      <a href={upgradeMailto(businessName)}>{t('plans.cta')}</a>
    </Button>
  )
}

function UpsellDialog({
  open,
  onOpenChange,
  feature,
  businessId,
  businessName,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  feature: string
  businessId?: string
  businessName?: string
}) {
  const {t} = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plan-upsell-dialog">
        <DialogHeader>
          <Tag tone="neutral" className="w-fit">
            {t('plans.badge')}
          </Tag>
          <DialogTitle className="mt-2">{t('plans.upsellTitle', {feature})}</DialogTitle>
          <DialogDescription>{t('plans.upsellBody')}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('plans.later')}
          </Button>
          <UpsellCta testId="plan-upsell-cta" businessId={businessId} businessName={businessName} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** A paid feature stays on screen, never hidden: the click opens the upsell instead. */
export function PlanGate({
  locked,
  feature,
  businessId,
  businessName,
  children,
}: {
  locked: boolean
  feature: string
  businessId?: string
  businessName?: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const {t} = useTranslation()
  if (!locked) return <>{children}</>
  return (
    <>
      <button
        type="button"
        data-testid="plan-gate-locked"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
        aria-label={t('plans.lockedAria', {feature})}>
        <LockGlyph size={14} className="shrink-0" />
        {feature}
        <Tag tone="neutral" className="ml-1">
          {t('plans.badge')}
        </Tag>
      </button>
      <UpsellDialog
        open={open}
        onOpenChange={setOpen}
        feature={feature}
        businessId={businessId}
        businessName={businessName}
      />
    </>
  )
}

/** Replaces a gated page's body: never a blank page or a silent 402. */
export function PlanLockedPage({
  feature,
  businessId,
  businessName,
}: {
  feature: string
  businessId?: string
  businessName?: string
}) {
  const {t} = useTranslation()
  return (
    <div
      className="surface-raised mx-auto flex max-w-lg flex-col items-center gap-4 p-8 text-center"
      data-testid="plan-locked-page">
      <span aria-hidden className="flex h-11 w-11 items-center justify-center rounded-full bg-accent text-primary">
        <LockGlyph size={20} className="shrink-0" />
      </span>
      <div className="flex items-center gap-2">
        <KairosMark className="text-primary" />
        <Tag tone="neutral">{t('plans.badge')}</Tag>
      </div>
      <h1 className="font-heading text-xl">{t('plans.upsellTitle', {feature})}</h1>
      <p className="text-sm text-muted-foreground">{t('plans.upsellBody')}</p>
      <UpsellCta testId="plan-locked-page-cta" businessId={businessId} businessName={businessName} />
    </div>
  )
}
